import React, { useState, useEffect } from 'react';
import { useJobs } from '../services/JobContext';
import { useNavigate, useParams } from 'react-router-dom';
import RoleGuard from './RoleGuard';
import { PERMISSIONS, ROLES } from '../utils/permissions';
import { JOB_TYPES } from '../constants';
import api from '../services/api';

const AddJob = () => {
  const { addJob, updateJob, jobs } = useJobs();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const isEditMode = !!jobId;
  
  const [formData, setFormData] = useState({
    companyName: '',
    position: '',
    bdName: '',
    payRange: '',
    jobType: 'W-2',
    resumeLink: '',
    jdLink: ''
  });
  const [bdOptions, setBdOptions] = useState([]);
  const [loadingBds, setLoadingBds] = useState(true);
  const [uploading, setUploading] = useState({
    resume: false,
    jd: false
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    resume: null,
    jd: null
  });

  useEffect(() => {
    const loadBds = async () => {
      setLoadingBds(true);
      try {
        const users = await api.getUsers();
        const bds = (users || [])
          .filter((u) => u.role === ROLES.BD)
          .map((u) => u.name || u.email)
          .filter(Boolean);

        // Use unique names
        const uniqueBds = Array.from(new Set(bds));
        setBdOptions(uniqueBds);
      } catch (error) {
        console.error('Failed to load BD users from API', error);
        setBdOptions([]);
      } finally {
        setLoadingBds(false);
      }
    };

    loadBds();
  }, []);

  useEffect(() => {
    if (isEditMode && jobId) {
      const job = jobs.find(j => j.id === jobId);
      if (job) {
        const resumeUrl = job.resumeLink || job.resume_link || '';
        const jdUrl = job.jdLink || job.jd_link || '';
        
        setFormData({
          companyName: job.companyName || '',
          position: job.position || '',
          bdName: job.bdName || '',
          payRange: job.payRange || '',
          jobType: job.jobType || 'W-2',
          resumeLink: resumeUrl,
          jdLink: jdUrl
        });
        
        // Set uploaded files state to show existing files
        if (resumeUrl) {
          setUploadedFiles(prev => ({ ...prev, resume: 'Existing file' }));
        }
        if (jdUrl) {
          setUploadedFiles(prev => ({ ...prev, jd: 'Existing file' }));
        }
      }
    }
  }, [isEditMode, jobId, jobs]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF, DOC, and DOCX files are allowed');
      e.target.value = '';
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 10MB');
      e.target.value = '';
      return;
    }

    // Upload file
    setUploading(prev => ({ ...prev, [fileType]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const endpoint = fileType === 'resume' ? '/api/v1/jobs/upload-resume' : '/api/v1/jobs/upload-jd';
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: uploadFormData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();

      // Update form with S3 URL
      const fieldName = fileType === 'resume' ? 'resumeLink' : 'jdLink';
      setFormData(prev => ({
        ...prev,
        [fieldName]: data.url
      }));

      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: data.filename
      }));

      alert(`${fileType === 'resume' ? 'Resume' : 'JD'} uploaded successfully!`);
    } catch (error) {
      console.error(`Failed to upload ${fileType}:`, error);
      alert(`Failed to upload ${fileType}: ${error.message}`);
      e.target.value = '';
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyName || !formData.position || !formData.bdName) {
      alert('Please fill in all required fields');
      return;
    }

    if (isEditMode) {
      await updateJob(jobId, formData);
      alert('Job updated successfully!');
    } else {
      await addJob(formData);
      alert('Job added successfully!');
    }
    navigate('/jobs');
  };

  return (
    <div>
      <RoleGuard permission={PERMISSIONS.CREATE_JOB}>
        <h1>{isEditMode ? 'Edit Job' : 'Add New Job'}</h1>
      
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-2">
            <div className="form-group">
              <label>Company Name *</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Position *</label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>BD Name *</label>
              <select
                name="bdName"
                value={formData.bdName}
                onChange={handleChange}
                required
                disabled={loadingBds}
              >
                <option value="">{loadingBds ? 'Loading BDs...' : 'Select BD'}</option>
                {bdOptions.map((bd) => (
                  <option key={bd} value={bd}>{bd}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Job Type</label>
              <select
                name="jobType"
                value={formData.jobType}
                onChange={handleChange}
              >
                {Object.entries(JOB_TYPES).map(([key, value]) => (
                  <option key={key} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Pay Range</label>
              <input
                type="text"
                name="payRange"
                value={formData.payRange}
                onChange={handleChange}
                placeholder="e.g., $80,000 - $100,000"
              />
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label>Resume File (PDF, DOC, DOCX)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileUpload(e, 'resume')}
                disabled={uploading.resume}
              />
              {uploading.resume && (
                <div style={{ marginTop: '8px', color: '#059669' }}>
                  ⏳ Uploading resume...
                </div>
              )}
              {uploadedFiles.resume && uploadedFiles.resume !== 'Existing file' && (
                <div style={{ marginTop: '8px', color: '#059669' }}>
                  ✓ {uploadedFiles.resume} uploaded successfully
                </div>
              )}
              {formData.resumeLink && uploadedFiles.resume === 'Existing file' && (
                <div style={{ marginTop: '8px', color: '#10b981', fontSize: '0.88rem', fontWeight: 500 }}>
                  ✓ Resume file attached (upload new file to replace)
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Job Description File (PDF, DOC, DOCX)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileUpload(e, 'jd')}
                disabled={uploading.jd}
              />
              {uploading.jd && (
                <div style={{ marginTop: '8px', color: '#059669' }}>
                  ⏳ Uploading JD...
                </div>
              )}
              {uploadedFiles.jd && uploadedFiles.jd !== 'Existing file' && (
                <div style={{ marginTop: '8px', color: '#059669' }}>
                  ✓ {uploadedFiles.jd} uploaded successfully
                </div>
              )}
              {formData.jdLink && uploadedFiles.jd === 'Existing file' && (
                <div style={{ marginTop: '8px', color: '#10b981', fontSize: '0.88rem', fontWeight: 500 }}>
                  ✓ JD file attached (upload new file to replace)
                </div>
              )}
            </div>
          </div>

          <div className="add-job-actions">
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update Job' : 'Add Job'}
            </button>
            <button 
              type="button" 
              className="btn" 
              onClick={() => navigate('/jobs')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
      </RoleGuard>
    </div>
  );
};

export default AddJob;